import { useEffect, useMemo, useRef } from 'react';
import { config } from '_config';
import { COUNTRY_CODE_AU, COUNTRY_CODE_NZ } from '_config/countryCodes';
import { useStoreValue } from 'store';
import { useAsyncScript } from './useAsyncScript';

export const ZENDESK_SNIPPET_ID = 'ze-snippet';
const zendeskWidgetKeys = {
  [COUNTRY_CODE_AU]: import.meta.env.VITE_ZENDESK_MESSAGING_KEY_AU,
  [COUNTRY_CODE_NZ]: import.meta.env.VITE_ZENDESK_MESSAGING_KEY_NZ,
};

// Messaging widget keys are public client identifiers embedded in Zendesk's browser snippet, not JWT signing secrets.
// https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/messaging-web-widget-qs/
export const getZendeskSnippetSrc = (countryCode = config.countryCode) => {
  const zendeskWidgetKey = zendeskWidgetKeys[countryCode];

  return zendeskWidgetKey ? `https://static.zdassets.com/ekr/snippet.js?key=${zendeskWidgetKey}` : '';
};

export const ZENDESK_CONVERSATION_FIELD_IDS = {
  pageTitle: import.meta.env.VITE_ZENDESK_CONVERSATION_FIELD_ID_PAGE_TITLE,
  productCode: import.meta.env.VITE_ZENDESK_CONVERSATION_FIELD_ID_PRODUCT_CODE,
  firstName: import.meta.env.VITE_ZENDESK_CONVERSATION_FIELD_ID_FIRST_NAME,
  lastName: import.meta.env.VITE_ZENDESK_CONVERSATION_FIELD_ID_LAST_NAME,
  applicationNumber: import.meta.env.VITE_ZENDESK_CONVERSATION_FIELD_ID_APPLICATION_NUMBER,
  email: import.meta.env.VITE_ZENDESK_CONVERSATION_FIELD_ID_EMAIL,
  mobileNumber: import.meta.env.VITE_ZENDESK_CONVERSATION_FIELD_ID_MOBILE_NUMBER,
};

const addField = (fields, id, value) => {
  if (id && value !== undefined && value !== null && value !== '') {
    fields.push({ id, value });
  }
};

const areConversationFieldsEqual = (previousFields, nextFields) =>
  previousFields?.length === nextFields.length &&
  previousFields.every(
    (previousField, index) =>
      previousField.id === nextFields[index].id && Object.is(previousField.value, nextFields[index].value),
  );

export const getZendeskConversationFields = (state, pageTitle, productCode = config.productCode) => {
  const contactDetails = state.acquisition?.contactDetails || {};
  const personalDetails = state.acquisition?.personalDetails || {};
  const fields = [];

  addField(fields, ZENDESK_CONVERSATION_FIELD_IDS.pageTitle, pageTitle);
  addField(fields, ZENDESK_CONVERSATION_FIELD_IDS.productCode, productCode);

  // WC-3585 records approval to conditionally send these user-provided values to Zendesk Messaging.
  // Zendesk maps them to custom ticket fields; no JWT, signing secret, or authentication credential is included.
  // https://developer.zendesk.com/documentation/zendesk-web-widget-sdks/sdks/web/conversation-metadata/
  addField(fields, ZENDESK_CONVERSATION_FIELD_IDS.firstName, personalDetails.firstName);
  addField(fields, ZENDESK_CONVERSATION_FIELD_IDS.lastName, personalDetails.lastName);
  addField(fields, ZENDESK_CONVERSATION_FIELD_IDS.applicationNumber, state.applicationRef);
  addField(fields, ZENDESK_CONVERSATION_FIELD_IDS.email, contactDetails.emailAddress);
  addField(fields, ZENDESK_CONVERSATION_FIELD_IDS.mobileNumber, contactDetails.mobileNumber);

  return fields;
};

export function useDigitalChat(pageTitle) {
  const [storeState] = useStoreValue();
  const lastSetConversationFields = useRef();
  const scriptStatus = useAsyncScript(getZendeskSnippetSrc(), ZENDESK_SNIPPET_ID);
  const conversationFields = useMemo(() => getZendeskConversationFields(storeState, pageTitle), [
    pageTitle,
    storeState,
  ]);

  useEffect(() => {
    const zendeskApi = window.zE;

    if (scriptStatus !== 'ready' || typeof zendeskApi !== 'function') {
      return;
    }

    const lastSetFields = lastSetConversationFields.current;

    if (areConversationFieldsEqual(lastSetFields, conversationFields)) {
      return;
    }

    const currentFieldIds = new Set(conversationFields.map(field => field.id));
    // Clear removed values so stale customer PII cannot carry into a later application in the same widget session.
    const clearedFields = (lastSetFields || [])
      .filter(field => !currentFieldIds.has(field.id))
      .map(field => ({ id: field.id, value: '' }));
    const updatedFields = [...conversationFields, ...clearedFields];

    if (updatedFields.length > 0) {
      zendeskApi('messenger:set', 'conversationFields', updatedFields);
    }

    lastSetConversationFields.current = conversationFields;
  }, [conversationFields, scriptStatus]);
}
